// frontend/src/pages/Admin.jsx
import React, { useState } from "react";
import AuditLogs from "./AuditLogs";
import {
  createOrganization,
  lookupOrganization,
  deleteOrganization,
} from "../api/admin";

const SECTIONS = [
  { key: "add", label: "Add organization" },
  { key: "remove", label: "Remove organization" },
  { key: "audit", label: "Audit trail" },
];

const DEFAULT_AUTH0_CONNECTION = "Username-Password-Authentication";

const EMPTY_ORG = {
  name: "",
  host: "",
  port: "5432",
  database: "",
  username: "",
  password: "",
  provider: "auth0",
  auth0Domain: "",
  auth0ClientId: "",
  auth0ClientSecret: "",
  auth0Connection: DEFAULT_AUTH0_CONNECTION,
  oktaDomain: "",
  oktaApiToken: "",
  oktaGroupId: "",
};

export default function Admin({ onOrgAdded, onOrgRemoved }) {
  const [section, setSection] = useState("add");

  return (
    <div>
      <ul className="nav nav-pills mb-3" role="tablist">
        {SECTIONS.map((s) => (
          <li className="nav-item" key={s.key}>
            <button
              id={`admin-${s.key}-tab`}
              className={"nav-link " + (section === s.key ? "active" : "")}
              role="tab"
              aria-selected={section === s.key}
              aria-controls={`admin-${s.key}-panel`}
              onClick={() => setSection(s.key)}
            >
              {s.label}
            </button>
          </li>
        ))}
      </ul>

      {section === "add" && (
        <div id="admin-add-panel" role="tabpanel" aria-labelledby="admin-add-tab">
          <AddOrganization onAdded={onOrgAdded} />
        </div>
      )}

      {section === "remove" && (
        <div id="admin-remove-panel" role="tabpanel" aria-labelledby="admin-remove-tab">
          <RemoveOrganization onRemoved={onOrgRemoved} />
        </div>
      )}

      {section === "audit" && (
        <div id="admin-audit-panel" role="tabpanel" aria-labelledby="admin-audit-tab">
          <AuditLogs />
        </div>
      )}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-2">
      <label className="form-label mb-1">{label}</label>
      {children}
      {hint && <div className="form-text">{hint}</div>}
    </div>
  );
}

function AddOrganization({ onAdded }) {
  const [form, setForm] = useState(EMPTY_ORG);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const auth0Domain = form.auth0Domain.trim();
  const preview =
    form.host.trim() && form.database.trim() && form.username.trim()
      ? `postgresql+psycopg2://${form.username.trim()}:••••••@${form.host.trim()}:${
          form.port || "5432"
        }/${form.database.trim()}?sslmode=require`
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOk("");

    const payload = {
      name: form.name,
      host: form.host,
      port: Number(form.port),
      database: form.database,
      username: form.username,
      password: form.password,
      provider: form.provider,
    };
    if (form.provider === "auth0") {
      payload.auth0 = {
        domain: form.auth0Domain,
        client_id: form.auth0ClientId,
        client_secret: form.auth0ClientSecret,
        connection: form.auth0Connection,
      };
    } else {
      payload.okta = {
        domain: form.oktaDomain,
        api_token: form.oktaApiToken,
        group_id: form.oktaGroupId,
      };
    }

    try {
      const created = await createOrganization(payload);
      setForm(EMPTY_ORG);
      setOk(`Added ${created.name} (org id ${created.id}).`);
      if (onAdded) onAdded(created);
    } catch (err) {
      setError(err?.message || "Failed to add organization");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row">
      <div className="col-12 col-lg-8 col-xl-7">
        <div className="card shadow-sm">
          <div className="card-header py-2">Add New Organization</div>
          <div className="card-body">
            <form onSubmit={onSubmit} autoComplete="off">
              <Field label="Organization name">
                <input
                  className="form-control"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="acme"
                  required
                />
              </Field>

              <div className="row g-2">
                <div className="col-12 col-md-9">
                  <Field label="PostgreSQL server">
                    <input
                      className="form-control"
                      value={form.host}
                      onChange={set("host")}
                      placeholder="example.postgres.database.azure.com"
                      required
                    />
                  </Field>
                </div>
                <div className="col-12 col-md-3">
                  <Field label="Port">
                    <input
                      className="form-control"
                      type="number"
                      min="1"
                      max="65535"
                      value={form.port}
                      onChange={set("port")}
                      required
                    />
                  </Field>
                </div>
              </div>

              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <Field label="Database name">
                    <input
                      className="form-control"
                      value={form.database}
                      onChange={set("database")}
                      placeholder="postgres"
                      required
                    />
                  </Field>
                </div>
                <div className="col-12 col-md-6">
                  <Field label="Username">
                    <input
                      className="form-control"
                      value={form.username}
                      onChange={set("username")}
                      autoComplete="off"
                      required
                    />
                  </Field>
                </div>
              </div>

              <Field
                label="Password"
                hint={preview && <span className="font-monospace">{preview}</span>}
              >
                <input
                  className="form-control"
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  autoComplete="new-password"
                  required
                />
              </Field>

              <hr />

              <Field label="Identity provider">
                <select
                  className="form-select"
                  value={form.provider}
                  onChange={set("provider")}
                >
                  <option value="auth0">Auth0</option>
                  <option value="okta">Okta</option>
                </select>
              </Field>

              {form.provider === "auth0" ? (
                <>
                  <Field
                    label="Auth0 domain"
                    hint={auth0Domain && `Audience: https://${auth0Domain}/api/v2/`}
                  >
                    <input
                      className="form-control"
                      value={form.auth0Domain}
                      onChange={set("auth0Domain")}
                      placeholder="acme.us.auth0.com"
                      required
                    />
                  </Field>
                  <Field label="Client ID">
                    <input
                      className="form-control"
                      value={form.auth0ClientId}
                      onChange={set("auth0ClientId")}
                      required
                    />
                  </Field>
                  <Field label="Client secret">
                    <input
                      className="form-control"
                      type="password"
                      value={form.auth0ClientSecret}
                      onChange={set("auth0ClientSecret")}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                  <Field label="Connection">
                    <input
                      className="form-control"
                      value={form.auth0Connection}
                      onChange={set("auth0Connection")}
                      required
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Okta domain">
                    <input
                      className="form-control"
                      value={form.oktaDomain}
                      onChange={set("oktaDomain")}
                      placeholder="rohirrim.okta-gov.com"
                      required
                    />
                  </Field>
                  <Field label="API token (SSWS)">
                    <input
                      className="form-control"
                      type="password"
                      value={form.oktaApiToken}
                      onChange={set("oktaApiToken")}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                  <Field label="Group ID">
                    <input
                      className="form-control"
                      value={form.oktaGroupId}
                      onChange={set("oktaGroupId")}
                      placeholder="00g..."
                      pattern="00g.*"
                      title="Okta group IDs start with 00g"
                      required
                    />
                  </Field>
                </>
              )}

              <div className="d-flex gap-2 align-items-center mt-3">
                <button className="btn btn-primary" type="submit" disabled={busy}>
                  {busy ? "Adding..." : "Add Organization"}
                </button>
                {ok && <span className="text-success small">{ok}</span>}
              </div>
              {error && <div className="text-danger small mt-2">{error}</div>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function RemoveOrganization({ onRemoved }) {
  const [name, setName] = useState("");
  const [lookup, setLookup] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const matches = lookup?.matches || [];
  const selected = matches.find((m) => m.id === selectedId) || null;

  function resetLookup() {
    setLookup(null);
    setSelectedId(null);
    setConfirm("");
  }

  async function onFind(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOk("");
    resetLookup();
    try {
      const data = await lookupOrganization(name.trim());
      setLookup(data);
      if ((data.matches || []).length === 1) setSelectedId(data.matches[0].id);
    } catch (err) {
      setError(err?.message || "Lookup failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!selected || confirm !== selected.name) return;
    setBusy(true);
    setError("");
    setOk("");
    try {
      const res = await deleteOrganization(selected.id, confirm);
      const r = res.removed || {};
      setOk(
        `Deleted ${res.name} (org id ${res.id}). Also removed ` +
          `${r.organization_identity_providers ?? 0} identity provider row(s), ` +
          `${r.feature_flags ?? 0} feature flag row(s), ` +
          `${r.cluster_connections ?? 0} cluster connection row(s).`
      );
      setName("");
      resetLookup();
      if (onRemoved) onRemoved(res.id);
    } catch (err) {
      setError(err?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row">
      <div className="col-12 col-lg-8 col-xl-7">
        <div className="card shadow-sm">
          <div className="card-header py-2">Remove Organization</div>
          <div className="card-body">
            <form className="input-group mb-3" onSubmit={onFind}>
              <input
                className="form-control"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  resetLookup();
                }}
                placeholder="Organization name"
                required
              />
              <button className="btn btn-outline-secondary" type="submit" disabled={busy}>
                Find
              </button>
            </form>

            {matches.length > 1 && (
              <div className="mb-3">
                <div className="small text-muted mb-1">
                  {matches.length} organizations match that name. Choose one:
                </div>
                {matches.map((m) => (
                  <div className="form-check" key={m.id}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`remove-org-${m.id}`}
                      checked={selectedId === m.id}
                      onChange={() => {
                        setSelectedId(m.id);
                        setConfirm("");
                      }}
                    />
                    <label className="form-check-label" htmlFor={`remove-org-${m.id}`}>
                      {m.name} (org id {m.id})
                    </label>
                  </div>
                ))}
              </div>
            )}

            {selected && (
              <>
                <table className="table table-sm mb-3">
                  <tbody>
                    <tr>
                      <th style={{ width: "40%" }}>organizations</th>
                      <td>
                        id {selected.id}, {selected.name}
                        <div className="small text-muted font-monospace">
                          {selected.host || "?"} / {selected.database || "?"}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>organization_identity_providers</th>
                      <td>
                        {selected.identity_providers.length === 0
                          ? "none"
                          : selected.identity_providers.map((p) => (
                              <div key={p.id}>
                                id {p.id}, {p.provider}
                                {p.is_active ? "" : " (inactive)"}
                              </div>
                            ))}
                      </td>
                    </tr>
                    <tr>
                      <th>feature_flags</th>
                      <td>{selected.feature_flag_count} row(s)</td>
                    </tr>
                    <tr>
                      <th>cluster_connections</th>
                      <td>{selected.cluster_connection_count} row(s)</td>
                    </tr>
                  </tbody>
                </table>

                {(lookup.unlinked_identity_providers || []).length > 0 && (
                  <div className="alert alert-warning py-2 small">
                    Identity provider rows named "{name.trim()}" are attached to other
                    organizations and will NOT be removed:{" "}
                    {lookup.unlinked_identity_providers
                      .map((p) => `id ${p.id} (org id ${p.organization_id})`)
                      .join(", ")}
                  </div>
                )}

                <div className="alert alert-danger py-2 small">
                  This permanently deletes the organizations row. The database cascade
                  removes every row listed above with it. Existing audit entries keep
                  their details but lose the organization link. This cannot be undone.
                </div>

                <Field
                  label={
                    <>
                      Type <strong>{selected.name}</strong> to confirm
                    </>
                  }
                >
                  <input
                    className="form-control"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="off"
                  />
                </Field>
                <button
                  type="button"
                  className="btn btn-danger mt-2"
                  disabled={busy || confirm !== selected.name}
                  onClick={onDelete}
                >
                  {busy ? "Deleting..." : "Delete organization"}
                </button>
              </>
            )}

            {ok && <div className="text-success small mt-2">{ok}</div>}
            {error && <div className="text-danger small mt-2">{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
